import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getPublishableKey } from '../context/StripeContext';

interface StripeConfigResponse {
  success: boolean;
  publishable_key: string;
  account_type?: 'platform' | 'connected';
  account_id?: string;
  error?: string;
}

class DynamicStripeService {
  private static instance: DynamicStripeService;
  private stripeCache = new Map<string, Promise<Stripe | null>>();
  
  private constructor() {}
  
  static getInstance(): DynamicStripeService {
    if (!this.instance) {
      this.instance = new DynamicStripeService();
    }
    return this.instance;
  }
  
  /**
   * Extract PaymentIntent ID from client_secret (matching mobile apps)
   */
  extractPaymentIntentId(clientSecret: string): string {
    return clientSecret.split('_secret_')[0];
  }
  
  /**
   * Get proper Stripe configuration for a payment (matching mobile apps)
   */
  async configureForPayment(clientSecret: string): Promise<Stripe | null> {
    const paymentIntentId = this.extractPaymentIntentId(clientSecret);
    
    // Check cache first
    const cacheKey = paymentIntentId;
    if (this.stripeCache.has(cacheKey)) {
      return this.stripeCache.get(cacheKey)!;
    }
    
    console.log('🔧 DYNAMIC STRIPE: Fetching config for PaymentIntent:', paymentIntentId);
    
    const stripePromise = this.fetchStripeConfig(paymentIntentId).catch((error) => {
      // Don't cache failures; allow a retry on the next attempt.
      this.stripeCache.delete(cacheKey);
      throw error;
    });
    this.stripeCache.set(cacheKey, stripePromise);

    return stripePromise;
  }
  
  private async fetchStripeConfig(paymentIntentId: string): Promise<Stripe | null> {
    try {
      const response = await fetch('/api/stripe/account/publishable-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_intent_id: paymentIntentId
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const config: StripeConfigResponse = await response.json();
      
      if (!config.success) {
        throw new Error(config.error || 'API returned error');
      }

      if (!config.publishable_key) {
        throw new Error('API response missing publishable key');
      }

      console.log('🔧 DYNAMIC STRIPE: Config received:', {
        account_type: config.account_type,
        account_id: config.account_id,
        publishable_key_preview: config.publishable_key.substring(0, 20) + '...'
      });
      
      // Configure Stripe like mobile apps do
      if (config.account_type === 'connected' && config.account_id) {
        // Use platform key with connected account (like iOS/Android)
        console.log('🔧 DYNAMIC STRIPE: ✅ Configuring for connected account:', config.account_id);
        return await loadStripe(config.publishable_key, {
          stripeAccount: config.account_id
        });
      } else {
        // Platform account
        console.log('🔧 DYNAMIC STRIPE: ✅ Configuring for platform account');
        return await loadStripe(config.publishable_key);
      }
      
    } catch (error) {
      console.error('🔧 DYNAMIC STRIPE: ❌ Config failed:', error);
      return this.fallbackToPlatformStripe();
    }
  }
  
  /**
   * Fallback to platform Stripe (matching mobile apps)
   */
  private async fallbackToPlatformStripe(): Promise<Stripe | null> {
    console.log('🔧 DYNAMIC STRIPE: 🔄 Falling back to platform Stripe');

    const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (envKey) {
      return await loadStripe(envKey);
    }

    // No env override: fetch the platform key from the server config endpoint.
    // If this also fails, let the error propagate so callers surface the
    // failure instead of silently using a stale key.
    const platformKey = await getPublishableKey();
    return await loadStripe(platformKey);
  }
  
  /**
   * Clear cache (useful for testing)
   */
  clearCache(): void {
    this.stripeCache.clear();
  }
}

export default DynamicStripeService;